import { useEffect, useState } from 'react';

import { DateRange } from 'react-day-picker';

import useOffers from 'api/useOffers';
import {
  ExtendedDateRange,
  FormikProps,
  ScheduleTimeFrame
} from 'common/contracts';
import { EScheduleType, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { PlusCircle } from 'lucide-react';
import { getDurationTextFromMinutes } from 'utils/formattingUtil';

import Button from 'components/Button/Button';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode, DatePickerValue } from 'components/DatePicker/DatePicker.types';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import Typography from 'components/Typography/Typography';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';

import { EToasterStatus, showToast } from '../../../../utils/showToast';
import ScheduleAppearancesTable from '../../components/ScheduleAppearancesTable';
import ScheduleStatusToggle from '../../components/ScheduleStatusToggle';
import { SCHEDULE_TAB_TRIGGERS } from '../../constants';

interface ScheduleTabProps {
  formikProps: FormikProps;
  rollingOfferId?: string;
  edit: boolean;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({
  formikProps,
  rollingOfferId,
  edit
}) => {
  const { values, setFieldValue } = formikProps;

  const { updateOffer } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });

  const [appearancesTableData, setAppearancesTableData] = useState<ScheduleTimeFrame[]>([]);
  const [editRangeDatesValues, setEditRangeDatesValues] = useState<ExtendedDateRange | null>(null);
  const [localDateRange, setLocalDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [localNotes, setLocalNotes] = useState('');
  const [formattedDuration, setFormattedDuration] = useState<string | null>(null);

  const activeTab = values.schedule?.permanent ? EScheduleType.PERMANENT : EScheduleType.SCHEDULED;

  useEffect(() => {
    const timeFramesData: ScheduleTimeFrame[] =
      values.schedule?.timeFrames?.map(
        (date: ScheduleTimeFrame, index: number) => ({
          id: date.id || `id-${index}`,
          startTime: date.startTime,
          endTime: date.endTime,
          notes: date.notes
        })
      );
    setAppearancesTableData(timeFramesData || []);
  }, [values?.schedule?.timeFrames]);

  useEffect(() => {
    if (localDateRange.from && localDateRange.to) {
      const diffMinutes = dayjs(localDateRange.to).diff(dayjs(localDateRange.from), 'minute');
      const duration = getDurationTextFromMinutes(diffMinutes);
      setFormattedDuration(duration);
    } else {
      setFormattedDuration(null);
    }
  }, [localDateRange]);

  useEffect(() => {
    if (editRangeDatesValues) {
      const [startDate, endDate, notes] = editRangeDatesValues;
      setLocalDateRange({ from: startDate, to: endDate });
      setLocalNotes(notes || '');
    } else {
      setLocalDateRange({ from: undefined, to: undefined });
      setLocalNotes('');
    }
  }, [editRangeDatesValues]);

  const resetScheduleValues = () => {
    setLocalDateRange({ from: undefined, to: undefined });
    setEditRangeDatesValues(null);
    setLocalNotes('');
  };

  const updateOfferStatus = async (active: boolean) => {
    if (edit) {
      try {
        await updateOffer.mutateAsync({
          offerId: String(rollingOfferId),
          form: { active: active }
        });
        setFieldValue('active', active);
      } catch (error) {
        showToast({
          message: 'Error editing rolling offer',
          status: EToasterStatus.ERROR
        });
      }
    } else {
      setFieldValue('active', active);
    }
  };

  const handleTabChange = (value: string) => {
    const isPermanent = value === EScheduleType.PERMANENT;
    setFieldValue('schedule', {
      timeFrames: values?.schedule?.timeFrames || [],
      permanent: isPermanent
    });
    resetScheduleValues();
  };

  const handleApply = () => {
    const fromUtcDate = localDateRange.from;
    const toUtcDate = localDateRange.to;

    if (fromUtcDate && toUtcDate) {
      const newTimeFrame: ScheduleTimeFrame = {
        startTime: new Date(fromUtcDate.toISOString()),
        endTime: new Date(toUtcDate.toISOString()),
        notes: localNotes
      };

      if (editRangeDatesValues) {
        const [editStartTime, editEndTime] = editRangeDatesValues;
        const editStartTimestamp = editStartTime.getTime();
        const editEndTimestamp = editEndTime.getTime();

        const updatedTimeFrames = values?.schedule?.timeFrames.map(
          (timeFrame: ScheduleTimeFrame) => {
            const currentStartTimestamp = new Date(timeFrame.startTime).getTime();
            const currentEndTimestamp = new Date(timeFrame.endTime).getTime();

            return currentStartTimestamp === editStartTimestamp &&
              currentEndTimestamp === editEndTimestamp
              ? newTimeFrame
              : timeFrame;
          }
        );
        setFieldValue('schedule', {
          permanent: false,
          timeFrames: updatedTimeFrames
        });
      } else {
        setFieldValue('schedule', {
          permanent: false,
          timeFrames: [...(values?.schedule?.timeFrames || []), newTimeFrame]
        });
      }

      resetScheduleValues();
    }
  };

  const handleCancel = () => {
    resetScheduleValues();
  };

  const handleEditTimeframe = (
    startTime: Date | string,
    endTime: Date | string,
    notes: string
  ) => {
    const start = startTime instanceof Date ? startTime : new Date(startTime);
    const end = endTime instanceof Date ? endTime : new Date(endTime);
    setEditRangeDatesValues([start, end, notes]);
  };

  const isApplyDisabled = !localDateRange.from || !localDateRange.to;
  const buttonLabel = editRangeDatesValues ? 'Apply' : 'Add Appearance';

  return (
    <div className="flex flex-col pb-10">
      <RichText
        title="Scheduled offer"
        description="Set specific dates & hours to your offer"
        className="flex flex-col gap-1.5 mb-6"
      />

      <ScheduleStatusToggle
        isActive={values.active}
        onToggle={updateOfferStatus}
        dataTestId="rollingOfferActiveSwitchBtn"
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-zinc-100 p-1 rounded-xl grid grid-cols-2 h-10 w-full">
          {SCHEDULE_TAB_TRIGGERS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-zinc-900 text-zinc-500 rounded-lg font-normal h-8"
              data-testid={`rollingOfferScheduleTab-${tab.value}`}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={EScheduleType.PERMANENT} className="mt-6">
          <Typography className="text-sm text-zinc-500">
            Permanent events won't have an end date
          </Typography>
        </TabsContent>

        <TabsContent value={EScheduleType.SCHEDULED} className="mt-5">
          <div className="space-y-5">
            <DatePicker
              label="Pick A Date"
              labelSpacing="mb-2"
              shouldDisplayTime={true}
              shouldExcludePast={true}
              mode={DatePickerMode.RANGE}
              value={localDateRange}
              onApply={(value: DatePickerValue) => setLocalDateRange(value as DateRange)}
            />

            <Input
              id="duration"
              label="Duration"
              value={formattedDuration || ''}
              placeholder="Select dates to calculate duration"
              disabled
            />

            <Input
              id="notes"
              label="Note"
              name="notes"
              value={localNotes}
              placeholder="Write something..."
              onChange={(e) => setLocalNotes(e.target.value)}
            />

            <div className="space-y-3">
              {editRangeDatesValues && (
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="w-full"
                >
                  Cancel
                </Button>
              )}
              <Button
                disabled={isApplyDisabled}
                onClick={handleApply}
                variant="secondary"
                className="w-full"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {buttonLabel}
              </Button>
            </div>

            {appearancesTableData && appearancesTableData.length > 0 && (
              <ScheduleAppearancesTable
                formikProps={formikProps}
                appearancesTableData={appearancesTableData}
                onEdit={handleEditTimeframe}
                onDelete={resetScheduleValues}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ScheduleTab;
